/**
 * HEIGHT DATUM (2026-10-07, Matt — Newport Beach Zoning Code §20.30.050 Grade
 * Establishment + §20.30.060 Height Limits). Derived on every read, never stored
 * (the annotation doctrine) — ONE engine feeds the site-plan drawing and anything
 * that later wants the numbers (a schedule, a section line).
 *
 * 1. FOOTPRINT at the EXTERIOR FACE OF STUD (Matt: "we use the exterior face of studs
 *    always for these kinds of things — the finish can change over time, the structure
 *    remains"): the union of every proposed exterior wall's CORE band, gap-closed so
 *    butt-jointed corners read as one outline. Overhangs, balconies and at-grade decks
 *    are not walls and so never enter it. The code never defines "footprint".
 * 2. THE FOUR-SIDED POLYGON (§20.30.050.A.1): "most closely approximates the actual
 *    footprint ... not smaller than the footprint ... entirely within the buildable
 *    area" — the minimum-area rectangle enclosing the footprint (rotating calipers over
 *    its hull). Whether it lies inside the buildable envelope is CHECKED and reported,
 *    never silently forced.
 * 3. SLOPE (A.2): (highest corner − lowest corner) ÷ the horizontal distance between
 *    those two corners, on EXISTING grade — the pre-construction survey TIN before any
 *    grading modifier (Matt's answer; §20.70 "Grade, Existing").
 * 4. ESTABLISHED GRADE (B):
 *    ≤ 5% → one level plane at (A + B + C + D) / 4;
 *    > 5% → five evenly spaced points on EACH side property line, the first where the
 *           front setback line meets it, the last where the rear setback line meets it;
 *           each point joined to its partner across the lot. Between two cross-lines the
 *           four points are rarely coplanar — the surface BLENDS bilinearly across each
 *           band (Matt took the recommendation, 2026-10-07). Beyond the first/last
 *           cross-line (an eave reaching into a setback) the end band extends.
 *    flood hazard → top of slab (§20.30.060.B.3), a settings choice.
 * 5. HIGH-POINT CHECK (§20.30.060.B.2): every vertex of every proposed building solid
 *    measured above the established grade directly below it. A point covered by a
 *    sloped roof face (pitch ≥ 3:12 — the post-2010 sloped-roof rule, NOT YET CONFIRMED
 *    against the district standards) is held to the sloped limit; anything else —
 *    flat roofs, parapets, guardrails — to the flat limit. The worst point of each
 *    class is reported with its margin.
 *
 * All z values are PROJECT feet; real-world display is the caller's (datums.ts).
 */
import type { Pt2, ModelDoc, ResolvedElement, SiteRecord, HeightDatumSettings } from '@aj/bim-kernel'
import {
  resolveAllElements, listSiteRecords, flattenSiteRing, resolveEdgeSetbacksFt, siteEnvelope,
  flattenPathPts, toProjectFt, getHeightDatumSettings, getType, assemblyThickness,
} from '@aj/bim-kernel'
import { buildExistingTin, tinZAt, topoPointsOf, topoBreaklinesOf, type TopoTin } from './categories/topo/tin'
import { wallLayerBands } from './categories/wall/generate2d'
import { roofSkeletonClipped } from './categories/roof/skeleton'
import { unionClosedGaps, clipperReady, inflateClosedRing, intersectClosed } from './clipper'
import type { ElementSolid } from './categories/wall/generate3d'

/** the code's method threshold: polygon slope above this uses the side-line plane */
export const HEIGHT_SLOPE_THRESHOLD = 0.05
/** sloped-roof pitch threshold (3:12) — see header, item 5 */
export const SLOPED_ROOF_MIN_PITCH = 0.25
/** categories whose solids count toward building height */
/** the chosen polygon may be at most this many times the area of the smallest polygon
 *  around the footprint (Matt, 2026-10-07: 20%) — it must "most closely approximate" the
 *  footprint, so it may turn and shift to find better ground but not balloon */
export const POLYGON_AREA_CAP = 1.2
/** a fireplace object's flue pieces (generate3d piece names) and chimney-ish type names */
const FLUE_RE = /flue|chimney|spark/i
const CHIMNEY_TYPE_RE = /chimney|spark arrest/i
/** how far from a flue a wall still counts as its chase */
const CHIMNEY_REACH_FT = 3
const HEIGHT_CATEGORIES = new Set(['wall', 'roof', 'railing', 'column', 'beam', 'stair', 'ramp', 'floor', 'ceiling'])

export type HeightMethod = 'flat' | 'slope' | 'slab'

export interface GradeSample { pt: Pt2; zFt: number; approx: boolean }

export interface HeightCheck {
  /** 'flat' = held to the flat limit, 'sloped' = under a sloped roof face */
  kind: 'flat' | 'sloped'
  /** what the point is: the highest roof point, the highest chimney point, or the
   *  governing point of a class */
  role: 'roof' | 'chimney' | 'other'
  /** extra height the code allows this piece over the limit (chimneys: 2') */
  allowanceFt: number
  elementId: string
  category: string
  pt: Pt2
  zFt: number
  /** the established grade directly below */
  gradeFt: number
  /** height above the established grade */
  heightFt: number
  limitFt: number
  /** limit + allowance − height: negative = OVER even with the allowance */
  marginFt: number
}

export interface SidePoint { pt: Pt2; zFt: number; approx: boolean }

/** the HOA (CC&R) check: the same established grade, its own limit; chimneys only when
 *  the CC&Rs limit them (allowance set), else not held to it at all */
export interface HoaHeightCheck {
  name: string
  /** which of the HOA's rules applies (e.g. "Two-story lots") */
  label: string
  citation: string
  limitFt: number
  /** what a chimney may rise over the HOA limit; null = chimneys are not limited */
  allowanceFt: number | null
  /** the roof point standing TALLEST above the established grade */
  roof: HeightCheck | null
  /** the chimney point standing tallest above the established grade */
  chimney: HeightCheck | null
}

/** the method AlderCAD did NOT choose, for the record on the drawing */
export interface HeightAlternative {
  method: 'flat' | 'slope'
  slope: number
  levelGradeFt: number | null
  /** room left at the tightest point under that option (negative = over) */
  tightestMarginFt: number | null
  /** the same for the chosen option */
  chosenTightestMarginFt: number | null
}

export type HeightDatumResult =
  | { ok: false; reason: 'noTopo' | 'noFootprint' | 'noRule'; problems?: string[] }
  | {
      ok: true
      settings: HeightDatumSettings
      /** the principal building's stud-face footprint (one ring) */
      footprint: Pt2[][]
      /** separate wall groups left out of the polygon (site walls, detached structures) */
      others: Pt2[][]
      /** the four-sided polygon, corners A B C D (counter-clockwise) with existing grade */
      corners: GradeSample[]
      /** polygon slope, fraction (0.032 = 3.2%), with the two corners it was read between */
      slope: number
      slopeHigh: number
      slopeLow: number
      /** the method the code calls for, before any settings override */
      codeMethod: 'flat' | 'slope'
      /** the method actually used */
      method: HeightMethod
      /** flat / slab: the one established grade elevation */
      levelGradeFt: number | null
      /** slope: the five points on each side line, front → rear */
      side1: SidePoint[] | null
      side2: SidePoint[] | null
      /** established grade at any plan point */
      gradeAt: (pt: Pt2) => number
      /** the polygon sits inside the buildable envelope (null = no setbacks to test) */
      insideBuildable: boolean | null
      /** worst point of each class (null = nothing of that class) */
      worstFlat: HeightCheck | null
      worstSloped: HeightCheck | null
      /** the single highest point of any roof */
      highestRoof: HeightCheck | null
      /** the single highest point of any chimney (its cap, shroud or spark arrestor
       *  when modeled), held to the limit + 2' */
      highestChimney: HeightCheck | null
      /** the chosen polygon's area, the smallest enclosing polygon's, and the cap */
      polygonAreaSf: number
      smallestPolygonAreaSf: number
      areaCap: number
      /** the other method, when both were possible and compared (auto only) */
      alternative: HeightAlternative | null
      /** the HOA limit, when the project has one */
      hoa: HoaHeightCheck | null
      /** things the reader must know: approximations, fallbacks, missing inputs */
      notes: string[]
    }

// ---------- small geometry ----------

function signedArea(r: Pt2[]): number {
  let a = 0
  for (let i = 0, j = r.length - 1; i < r.length; j = i++) a += (r[j][0] * r[i][1] - r[i][0] * r[j][1])
  return a / 2
}

function pointInRing(pt: Pt2, ring: Pt2[]): boolean {
  let inside = false
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i], [xj, yj] = ring[j]
    if ((yi > pt[1]) !== (yj > pt[1]) && pt[0] < ((xj - xi) * (pt[1] - yi)) / (yj - yi) + xi) inside = !inside
  }
  return inside
}

function distToRing(pt: Pt2, ring: Pt2[]): number {
  let best = Infinity
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const a = ring[j], b = ring[i]
    const dx = b[0] - a[0], dy = b[1] - a[1]
    const L2 = dx * dx + dy * dy
    const t = L2 > 1e-12 ? Math.max(0, Math.min(1, ((pt[0] - a[0]) * dx + (pt[1] - a[1]) * dy) / L2)) : 0
    const d = Math.hypot(a[0] + dx * t - pt[0], a[1] + dy * t - pt[1])
    if (d < best) best = d
  }
  return best
}

function convexHull(points: Pt2[]): Pt2[] {
  const pts = [...points].sort((a, b) => a[0] - b[0] || a[1] - b[1])
  if (pts.length < 3) return pts
  const cross = (o: Pt2, a: Pt2, b: Pt2) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0])
  const lower: Pt2[] = []
  for (const p of pts) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 1e-12) lower.pop()
    lower.push(p)
  }
  const upper: Pt2[] = []
  for (let i = pts.length - 1; i >= 0; i--) {
    const p = pts[i]
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 1e-12) upper.pop()
    upper.push(p)
  }
  upper.pop(); lower.pop()
  return lower.concat(upper)
}

/** minimum-area enclosing rectangle (rotating calipers), corners counter-clockwise */
export function minAreaRect(points: Pt2[]): Pt2[] | null {
  const hull = convexHull(points)
  if (hull.length < 3) return null
  let best: { area: number; rect: Pt2[] } | null = null
  for (let i = 0; i < hull.length; i++) {
    const a = hull[i], b = hull[(i + 1) % hull.length]
    const L = Math.hypot(b[0] - a[0], b[1] - a[1])
    if (L < 1e-9) continue
    const ux = (b[0] - a[0]) / L, uy = (b[1] - a[1]) / L
    let u0 = Infinity, u1 = -Infinity, v0 = Infinity, v1 = -Infinity
    for (const p of hull) {
      const u = p[0] * ux + p[1] * uy, v = -p[0] * uy + p[1] * ux
      if (u < u0) u0 = u; if (u > u1) u1 = u
      if (v < v0) v0 = v; if (v > v1) v1 = v
    }
    const area = (u1 - u0) * (v1 - v0)
    if (!best || area < best.area - 1e-9) {
      const at = (u: number, v: number): Pt2 => [u * ux - v * uy, u * uy + v * ux]
      best = { area, rect: [at(u0, v0), at(u1, v0), at(u1, v1), at(u0, v1)] }
    }
  }
  return best ? best.rect : null
}

/**
 * THE FOUR-SIDED POLYGON (§20.30.050.A.1): the smallest-area quadrilateral that
 * encloses the footprint AND lies inside the buildable envelope. The code asks for a
 * four-sided polygon, not a rectangle — a square-cornered box around a house on a lot
 * with an angled setback line can poke past it (measured on a real project: 2.5 ft),
 * while a box with one side tilted to that line fits. Every side is a SUPPORTING line
 * of the footprint's hull, in one of these directions: a hull edge, an envelope edge,
 * or the minimum rectangle's; every convex choice of four is tried. `fits` is false
 * when no enclosing polygon fits inside (the building itself reaches into a setback) —
 * the smallest one is returned anyway, and the caller says so.
 */
const FIT_TOL_FT = 0.25

/** how far the footprint reaches past the envelope (0 = inside, within tolerance) */
export function setbackOvershootFt(footprint: Pt2[], envelope: Pt2[]): number {
  let worst = 0
  for (const p of footprint) if (!pointInRing(p, envelope)) worst = Math.max(worst, distToRing(p, envelope))
  return worst > FIT_TOL_FT ? worst : 0
}

export function fourSidedPolygon(footprint: Pt2[], envelope: Pt2[] | null): { quad: Pt2[]; fits: boolean | null } | null {
  const hull = convexHull(footprint)      // counter-clockwise
  if (hull.length < 3) return null
  const rect = minAreaRect(hull)
  const angles: number[] = []
  const addEdges = (ring: Pt2[]) => {
    const ccw = signedArea(ring) >= 0 ? ring : [...ring].reverse()
    for (let i = 0; i < ccw.length; i++) {
      const a = ccw[i], b = ccw[(i + 1) % ccw.length]
      if (Math.hypot(b[0] - a[0], b[1] - a[1]) < 0.5) continue
      angles.push(Math.atan2(-(b[0] - a[0]), b[1] - a[1]))   // outward normal (dy, -dx)
    }
  }
  addEdges(hull)
  if (rect) addEdges(rect)
  if (envelope) addEdges(envelope)
  const norm = (a: number) => { let x = a % (2 * Math.PI); if (x < 0) x += 2 * Math.PI; return x }
  const sorted = [...new Set(angles.map((a) => Math.round(norm(a) * 2000) / 2000))].sort((a, b) => a - b)
  const N = sorted.map((a) => {
    const n: Pt2 = [Math.cos(a), Math.sin(a)]
    let d = -Infinity
    for (const p of hull) d = Math.max(d, n[0] * p[0] + n[1] * p[1])
    return { a, n, d }
  })
  const meet = (u: typeof N[number], v: typeof N[number]): Pt2 | null => {
    const det = u.n[0] * v.n[1] - u.n[1] * v.n[0]
    if (Math.abs(det) < 1e-9) return null
    return [(u.d * v.n[1] - v.d * u.n[1]) / det, (u.n[0] * v.d - v.n[0] * u.d) / det]
  }
  // 3" of drawing tolerance: a building drawn to its setback line measures a hair over
  // it at the stud face (measured on a real project: 1.3")
  const inside = (p: Pt2) => !envelope || pointInRing(p, envelope) || distToRing(p, envelope) < FIT_TOL_FT
  let bestFit: { area: number; quad: Pt2[] } | null = null
  let bestAny: { area: number; quad: Pt2[] } | null = null
  const k = N.length
  const gapOk = (x: number, y: number) => { const g = norm(y - x); return g > 1e-3 && g < Math.PI - 1e-3 }
  for (let i = 0; i < k; i++) for (let j = i + 1; j < k; j++) {
    if (!gapOk(N[i].a, N[j].a)) continue
    for (let l = j + 1; l < k; l++) {
      if (!gapOk(N[j].a, N[l].a)) continue
      for (let m = l + 1; m < k; m++) {
        if (!gapOk(N[l].a, N[m].a) || !gapOk(N[m].a, N[i].a)) continue
        const sides = [N[i], N[j], N[l], N[m]]
        const quad: Pt2[] = []
        for (let q = 0; q < 4; q++) {
          const c = meet(sides[q], sides[(q + 1) % 4])
          if (!c) break
          quad.push(c)
        }
        if (quad.length < 4) continue
        const area = Math.abs(signedArea(quad))
        if (!bestAny || area < bestAny.area - 1e-9) bestAny = { area, quad }
        if (bestFit && area >= bestFit.area - 1e-9) continue
        if (quad.every(inside)) bestFit = { area, quad }
      }
    }
  }
  if (bestFit) return { quad: bestFit.quad, fits: envelope ? true : null }
  if (bestAny) return { quad: bestAny.quad, fits: envelope ? false : null }
  return rect ? { quad: rect, fits: envelope ? false : null } : null
}

/**
 * The slope of a four-sided polygon (§20.30.050.A.2): highest corner − lowest corner over
 * the horizontal distance between them; on a tie (a polygon square to the fall has two
 * equally high corners) the CLOSEST such pair, which is the true fall across it.
 */
export function polygonSlope(corners: { pt: Pt2; zFt: number }[]): { slope: number; hi: number; lo: number } {
  const zMax = Math.max(...corners.map((c) => c.zFt)), zMin = Math.min(...corners.map((c) => c.zFt))
  let hi = 0, lo = 0, run = Infinity
  corners.forEach((a, i) => {
    if (zMax - a.zFt > 0.005) return
    corners.forEach((b, j) => {
      if (b.zFt - zMin > 0.005 || i === j) return
      const d = Math.hypot(a.pt[0] - b.pt[0], a.pt[1] - b.pt[1])
      if (d < run) { run = d; hi = i; lo = j }
    })
  })
  if (!Number.isFinite(run)) run = 0
  return { slope: run > 1e-6 ? (corners[hi].zFt - corners[lo].zFt) / run : 0, hi, lo }
}

/**
 * LEAST-STEEP POLYGON (Matt, 2026-10-07, from plan-check feedback: "the rear line can be
 * rotated to bring the far point in and the other point out a bit and still encompass the
 * building footprint but find a less steep portion of the site"). Starting from the
 * smallest enclosing polygon, each side is ROTATED about the building (it stays a supporting
 * line of the footprint, so the polygon always encloses it) in shrinking steps, keeping any
 * turn that lowers the slope, while the polygon stays convex, inside the buildable area, and
 * no bigger than `maxArea` (the code wants the polygon to "most closely approximate" the
 * footprint, so it may not balloon to find flat ground).
 */
export function leastSteepPolygon(
  footprint: Pt2[], envelope: Pt2[] | null, start: Pt2[],
  slopeOf: (quad: Pt2[]) => number, maxArea: number,
): Pt2[] {
  return tunePolygon(footprint, envelope, start, (q) => -slopeOf(q), maxArea)
}

/** the general form: turn the sides to RAISE `score` (higher is better) under the same
 *  constraints; a score of -Infinity marks a polygon as not allowed */
export function tunePolygon(
  footprint: Pt2[], envelope: Pt2[] | null, start: Pt2[],
  score: (quad: Pt2[]) => number, maxArea: number,
): Pt2[] {
  const hull = convexHull(footprint)
  if (hull.length < 3 || start.length !== 4) return start
  const ccw = signedArea(start) >= 0 ? start : [...start].reverse()
  const norm = (a: number) => { let x = a % (2 * Math.PI); if (x < 0) x += 2 * Math.PI; return x }
  const angOf = (a: Pt2, b: Pt2) => Math.atan2(-(b[0] - a[0]), b[1] - a[1])
  const inside = (p: Pt2) => !envelope || pointInRing(p, envelope) || distToRing(p, envelope) < FIT_TOL_FT
  const build = (angs: number[]): Pt2[] | null => {
    for (let i = 0; i < 4; i++) {
      const g = norm(angs[(i + 1) % 4] - angs[i])
      if (!(g > 1e-3 && g < Math.PI - 1e-3)) return null
    }
    const sides = angs.map((a) => {
      const n: Pt2 = [Math.cos(a), Math.sin(a)]
      let d = -Infinity
      for (const p of hull) d = Math.max(d, n[0] * p[0] + n[1] * p[1])
      return { n, d }
    })
    const quad: Pt2[] = []
    for (let q = 0; q < 4; q++) {
      const u = sides[q], v = sides[(q + 1) % 4]
      const det = u.n[0] * v.n[1] - u.n[1] * v.n[0]
      if (Math.abs(det) < 1e-9) return null
      quad.push([(u.d * v.n[1] - v.d * u.n[1]) / det, (u.n[0] * v.d - v.n[0] * u.d) / det])
    }
    if (Math.abs(signedArea(quad)) > maxArea + 1e-6) return null
    if (!quad.every(inside)) return null
    return quad
  }
  let angs = ccw.map((a, i) => angOf(a, ccw[(i + 1) % 4]))
  let best = build(angs) ?? ccw
  let bestS = score(best)
  for (const stepDeg of [8, 4, 2, 1, 0.5, 0.25, 0.1]) {
    const step = (stepDeg * Math.PI) / 180
    let improved = true
    while (improved) {
      improved = false
      for (let i = 0; i < 4; i++) {
        for (const sg of [1, -1]) {
          const next = angs.slice()
          next[i] += sg * step
          const quad = build(next)
          if (!quad) continue
          const s = score(quad)
          if (s > bestS + 1e-7) { angs = next; best = quad; bestS = s; improved = true }
        }
      }
    }
  }
  return best
}

/** intersection of segments p→q and r→s as (t on p→q, u on r→s), or null when parallel */
function segX(p: Pt2, q: Pt2, r: Pt2, s: Pt2): { t: number; u: number } | null {
  const ex = q[0] - p[0], ey = q[1] - p[1], fx = s[0] - r[0], fy = s[1] - r[1]
  const den = ex * fy - ey * fx
  if (Math.abs(den) < 1e-12) return null
  const dx = r[0] - p[0], dy = r[1] - p[1]
  return { t: (dx * fy - dy * fx) / den, u: (dx * ey - dy * ex) / den }
}

// ---------- existing grade ----------

/** the existing survey surface (topo points + breaklines, no grading), or null without topo */
export function existingGradeSampler(els: ResolvedElement[]): ((pt: Pt2) => { zFt: number; approx: boolean }) | null {
  const tins: TopoTin[] = []
  for (const el of els) {
    if (el.category !== 'topo') continue
    const tin = buildExistingTin(topoPointsOf(el), topoBreaklinesOf(el))
    if (tin) tins.push(tin)
  }
  if (!tins.length) return null
  return (pt: Pt2) => {
    for (const tin of tins) {
      const z = tinZAt(tin, pt)
      if (z !== null) return { zFt: z, approx: false }
    }
    // outside the survey: the nearest surveyed point, flagged
    let bd = Infinity, z = 0
    for (const tin of tins) {
      for (let i = 0; i < tin.pts.length; i++) {
        const d = Math.hypot(tin.pts[i][0] - pt[0], tin.pts[i][1] - pt[1])
        if (d < bd) { bd = d; z = tin.zs[i] }
      }
    }
    return { zFt: z, approx: true }
  }
}

// ---------- the footprint ----------

const demolished = (el: ResolvedElement) => !!el.phaseDemolished

/** the proposed building's outlines at the exterior face of stud, largest first. `keep`
 *  picks the walls (default: every exterior wall not demolished — the proposed building);
 *  the demolition tabulation passes the EXISTING building's walls, demolished ones included */
export function studFaceFootprint(els: ResolvedElement[], keep: (el: ResolvedElement) => boolean = (el) => !demolished(el)): Pt2[][] {
  if (!clipperReady()) return []
  const rings: Pt2[][] = []
  for (const el of els) {
    if (el.category !== 'wall' || !keep(el)) continue
    if ((el.typeParams as { function?: string })?.function === 'interior') continue
    const bands = wallLayerBands(el)?.filter((b) => b.isCore)
    if (!bands?.length) continue
    for (const b of bands) for (const r of b.rings) if (r.length >= 3) rings.push(r)
  }
  if (!rings.length) return []
  // close the butt-joint notches at corners (a stud face meets the next wall's stud
  // face across the corner) — a morphological close, which keeps real corners square
  const merged = unionClosedGaps(rings, 1)
  if (!merged.length) return []
  // keep the OUTER rings (the orientation of the largest one); holes are courtyards
  // and the wall cores' own inner faces
  let big = merged[0]
  for (const r of merged) if (Math.abs(signedArea(r)) > Math.abs(signedArea(big))) big = r
  const outerSign = Math.sign(signedArea(big))
  const outers = merged.filter((r) => Math.sign(signedArea(r)) === outerSign && Math.abs(signedArea(r)) > 4)
  // GARDEN WALLS: a thin wall run reaching out from the building (an 8" CMU planter or
  // site wall drawn as an exterior wall — measured on a real project: 40 ft of them
  // dragged the polygon 29 ft into a setback) is not building. A morphological OPENING
  // (shrink, then grow back, by OPEN_FT) removes anything narrower than 2 × OPEN_FT and
  // keeps the solid building, square corners included (miter joins); clipped back to
  // the original so it can never grow. An outline that is all thin (walls that never
  // close into a loop) keeps its original shape rather than vanishing.
  const OPEN_FT = 1.5
  const pieces: Pt2[][] = []
  for (const r of outers) {
    const ccw = signedArea(r) >= 0 ? r : [...r].reverse()
    const shrunk = inflateClosedRing(ccw, -OPEN_FT, 4)
    const grown = shrunk.flatMap((q) => inflateClosedRing(signedArea(q) >= 0 ? q : [...q].reverse(), OPEN_FT, 4))
    const opened = grown.length ? intersectClosed(grown, [ccw]).filter((q) => Math.abs(signedArea(q)) > 4) : []
    const kept = opened.reduce((a, q) => a + Math.abs(signedArea(q)), 0)
    if (kept < Math.abs(signedArea(r)) * 0.25) pieces.push(r)
    else pieces.push(...opened)
  }
  // largest first: [0] is the principal building, the rest are separate pieces
  // (site and garden walls, detached structures) the caller decides about
  return pieces.sort((a, b) => Math.abs(signedArea(b)) - Math.abs(signedArea(a)))
}

// ---------- the side-property-line plane ----------

/** each role's edges as one chain of points, in ring order */
function roleChain(rec: SiteRecord, role: string): Pt2[] {
  const { pts, edgeOf } = flattenSiteRing(rec)
  const n = pts.length
  // start the walk at a segment whose predecessor is NOT this role, so a chain that
  // wraps past index 0 still comes out contiguous
  const isRole = (i: number) => rec.edgeRoles?.[rec.path.nodes[edgeOf[i]].id] === role
  let start = -1
  for (let i = 0; i < n; i++) if (isRole(i) && !isRole((i - 1 + n) % n)) { start = i; break }
  if (start < 0) return []
  const out: Pt2[] = [pts[start]]
  for (let k = 0; k < n; k++) {
    const i = (start + k) % n
    if (!isRole(i)) break
    out.push(pts[(i + 1) % n])
  }
  return out
}

/** the setback line of a role: each of its segments moved inward by that edge's setback */
function setbackLine(rec: SiteRecord, role: string, inwardSign: number): Pt2[] {
  const { pts, edgeOf } = flattenSiteRing(rec)
  const setbacks = resolveEdgeSetbacksFt(rec)
  const n = pts.length
  const isRole = (i: number) => rec.edgeRoles?.[rec.path.nodes[edgeOf[i]].id] === role
  let start = -1
  for (let i = 0; i < n; i++) if (isRole(i) && !isRole((i - 1 + n) % n)) { start = i; break }
  if (start < 0) return []
  const segs: [Pt2, Pt2][] = []
  for (let k = 0; k < n; k++) {
    const i = (start + k) % n
    if (!isRole(i)) break
    const a = pts[i], b = pts[(i + 1) % n]
    const L = Math.hypot(b[0] - a[0], b[1] - a[1])
    if (L < 1e-9) continue
    // left normal × sign = inward
    const nx = (-(b[1] - a[1]) / L) * inwardSign, ny = ((b[0] - a[0]) / L) * inwardSign
    const d = setbacks[edgeOf[i]] ?? 0
    segs.push([[a[0] + nx * d, a[1] + ny * d], [b[0] + nx * d, b[1] + ny * d]])
  }
  if (!segs.length) return []
  // join consecutive offset segments at their line intersection (a mitre)
  const out: Pt2[] = [segs[0][0]]
  for (let k = 0; k < segs.length - 1; k++) {
    const x = segX(segs[k][0], segs[k][1], segs[k + 1][0], segs[k + 1][1])
    out.push(x ? [segs[k][0][0] + (segs[k][1][0] - segs[k][0][0]) * x.t, segs[k][0][1] + (segs[k][1][1] - segs[k][0][1]) * x.t] : segs[k][1])
  }
  out.push(segs[segs.length - 1][1])
  return out
}

/** arc-length station (along `chain`) where the setback line `line` — its end
 *  segments extended — crosses it; null when they never meet */
function stationOfCrossing(chain: Pt2[], line: Pt2[]): number | null {
  if (chain.length < 2 || line.length < 2) return null
  let best: { s: number; reach: number } | null = null
  let s0 = 0
  for (let i = 0; i < chain.length - 1; i++) {
    const p = chain[i], q = chain[i + 1]
    const len = Math.hypot(q[0] - p[0], q[1] - p[1])
    for (let k = 0; k < line.length - 1; k++) {
      const x = segX(p, q, line[k], line[k + 1])
      if (!x || x.t < -1e-6 || x.t > 1 + 1e-6) continue
      // the first/last setback segment may extend past its end; inner ones may not
      const lo = k === 0 ? -1e6 : -1e-6, hi = k === line.length - 2 ? 1e6 : 1 + 1e-6
      if (x.u < lo || x.u > hi) continue
      const reach = Math.max(0, -x.u, x.u - 1)
      const s = s0 + Math.max(0, Math.min(1, x.t)) * len
      if (!best || reach < best.reach) best = { s, reach }
    }
    s0 += len
  }
  return best ? best.s : null
}

function pointAtStation(chain: Pt2[], s: number): Pt2 {
  let acc = 0
  for (let i = 0; i < chain.length - 1; i++) {
    const p = chain[i], q = chain[i + 1]
    const len = Math.hypot(q[0] - p[0], q[1] - p[1])
    if (acc + len >= s - 1e-9 || i === chain.length - 2) {
      const t = len > 1e-9 ? Math.max(0, Math.min(1, (s - acc) / len)) : 0
      return [p[0] + (q[0] - p[0]) * t, p[1] + (q[1] - p[1]) * t]
    }
    acc += len
  }
  return chain[chain.length - 1]
}

/** five evenly spaced points along a side chain, from the front-setback crossing to
 *  the rear-setback crossing */
function fivePoints(chain: Pt2[], front: Pt2[], rear: Pt2[]): Pt2[] | null {
  const sf = stationOfCrossing(chain, front), sr = stationOfCrossing(chain, rear)
  if (sf === null || sr === null || Math.abs(sr - sf) < 1) return null
  return [0, 1, 2, 3, 4].map((k) => pointAtStation(chain, sf + ((sr - sf) * k) / 4))
}

/** z at `pt` over the bilinear patch A(0,0) B(1,0) C(1,1) D(0,1); also returns (u,v) */
function bilinear(pt: Pt2, A: Pt2, B: Pt2, C: Pt2, D: Pt2, zs: [number, number, number, number]) {
  let u = 0.5, v = 0.5
  for (let it = 0; it < 20; it++) {
    const x = (1 - u) * (1 - v) * A[0] + u * (1 - v) * B[0] + u * v * C[0] + (1 - u) * v * D[0] - pt[0]
    const y = (1 - u) * (1 - v) * A[1] + u * (1 - v) * B[1] + u * v * C[1] + (1 - u) * v * D[1] - pt[1]
    const xu = (1 - v) * (B[0] - A[0]) + v * (C[0] - D[0]), yu = (1 - v) * (B[1] - A[1]) + v * (C[1] - D[1])
    const xv = (1 - u) * (D[0] - A[0]) + u * (C[0] - B[0]), yv = (1 - u) * (D[1] - A[1]) + u * (C[1] - B[1])
    const det = xu * yv - xv * yu
    if (Math.abs(det) < 1e-12) break
    const du = (x * yv - xv * y) / det, dv = (xu * y - x * yu) / det
    u -= du; v -= dv
    if (Math.abs(du) + Math.abs(dv) < 1e-10) break
  }
  const z = (1 - u) * (1 - v) * zs[0] + u * (1 - v) * zs[1] + u * v * zs[2] + (1 - u) * v * zs[3]
  return { z, u, v }
}

/**
 * The sloped-site established grade (§20.30.050.B.3) as a function of plan position:
 * the four bands between the five cross-lines, each blended bilinearly (the four points
 * of a band are rarely coplanar); beyond the first / last cross-line the end band
 * extends. Exported so a drawing can rebuild the surface from a result that crossed the
 * worker boundary (functions do not clone).
 */
export function sideLinePlane(L: SidePoint[], R: SidePoint[]): (pt: Pt2) => number {
  return (pt: Pt2) => {
    let best: { z: number; miss: number } | null = null
    for (let k = 0; k < 4; k++) {
      const r = bilinear(pt, L[k].pt, R[k].pt, R[k + 1].pt, L[k + 1].pt, [L[k].zFt, R[k].zFt, R[k + 1].zFt, L[k + 1].zFt])
      // v runs front to rear inside band k; outside [0,1] only the end bands extend
      const lov = k === 0 ? -Infinity : 0, hiv = k === 3 ? Infinity : 1
      const miss = r.v < lov ? lov - r.v : r.v > hiv ? r.v - hiv : 0
      if (!best || miss < best.miss) best = { z: r.z, miss }
      if (miss === 0) break
    }
    return best ? best.z : 0
  }
}

/** the established grade at a plan point from any result, including one that came back
 *  from the worker without its `gradeAt` function */
export function gradeFromResult(r: { method: HeightMethod; levelGradeFt: number | null; side1: SidePoint[] | null; side2: SidePoint[] | null }): (pt: Pt2) => number {
  if (r.method === 'slope' && r.side1 && r.side2) return sideLinePlane(r.side1, r.side2)
  const g = r.levelGradeFt ?? 0
  return () => g
}

// ---------- the engine ----------

export function computeHeightDatum(
  model: ModelDoc,
  solids: ElementSolid[] | null,
  settingsIn?: HeightDatumSettings,
): HeightDatumResult {
  const settings = settingsIn ?? getHeightDatumSettings(model)
  // no city height rule on file for this parcel: nothing is measured against a guess
  if (!settings.ready) return { ok: false, reason: 'noRule', problems: settings.problems }
  const els = resolveAllElements(model)
  const byId = new Map<string, ResolvedElement>(els.map((e) => [e.id, e]))
  const grade = existingGradeSampler(els)
  if (!grade) return { ok: false, reason: 'noTopo' }
  const pieces = studFaceFootprint(els)
  // the PRINCIPAL structure is the largest enclosed outline; separate pieces (garden
  // and site walls, a detached garage or ADU — accessory structures are measured from
  // existing grade under their own rule, §20.30.060.B.3.c) stay out of its polygon
  const footprint = pieces.slice(0, 1)
  if (!footprint.length) return { ok: false, reason: 'noFootprint' }
  // the property line (the one under the building) and its buildable envelope
  const pls = listSiteRecords(model).filter((r) => r.kind === 'propertyLine' && r.path.closed && r.path.nodes.length >= 3)
  const fc = footprint[0].reduce((a, p) => [a[0] + p[0] / footprint[0].length, a[1] + p[1] / footprint[0].length] as Pt2, [0, 0] as Pt2)
  const pl = pls.find((r) => pointInRing(fc, flattenPathPts(r.path))) ?? pls[0] ?? null
  const env = pl ? siteEnvelope(pl) : null
  const envRing = env ? flattenPathPts(env) : null
  if (envRing && envRing.length > 1) {
    const a = envRing[0], b = envRing[envRing.length - 1]
    if (Math.hypot(a[0] - b[0], a[1] - b[1]) < 1e-9) envRing.pop()
  }
  const poly = fourSidedPolygon(footprint[0], envRing)
  if (!poly) return { ok: false, reason: 'noFootprint' }
  const insideBuildable = poly.fits
  const notes: string[] = []
  if (pieces.length > 1) notes.push(`${pieces.length - 1} separate wall group${pieces.length > 2 ? 's' : ''} (site walls or detached structures) left out of the polygon.`)

  if (insideBuildable === false) {
    const over = envRing ? setbackOvershootFt(footprint[0], envRing) : 0
    const inches = Math.round(over * 12)
    notes.push(over > 0
      ? `No four-sided polygon around the building fits inside the buildable area: the building (at face of stud) reaches ${Math.floor(inches / 12)}'-${inches % 12}" past a setback line.`
      : 'No four-sided polygon around the building fits inside the buildable area.')
  }
  if (!pl) notes.push('No property line drawn; the buildable-area check was skipped.')
  else if (!envRing) notes.push('The property line has no setbacks set; the buildable-area check was skipped.')

  // the five points on each side property line (independent of the polygon)
  const sidePts = pl ? (() => {
    const ring = flattenPathPts(pl.path)
    const inward = signedArea(ring) > 0 ? 1 : -1
    const front = setbackLine(pl, 'front', inward), rear = setbackLine(pl, 'rear', inward)
    const s1 = fivePoints(roleChain(pl, 'side1'), front, rear)
    const s2 = fivePoints(roleChain(pl, 'side2'), front, rear)
    return s1 && s2 ? { s1, s2 } : null
  })() : null
  const sideSamples = sidePts
    ? { s1: sidePts.s1.map((pt) => ({ pt, ...grade(pt) })), s2: sidePts.s2.map((pt) => ({ pt, ...grade(pt) })) }
    : null

  // ---- choosing the polygon (Matt, 2026-10-07: "optimize for most allowable height") ----
  // The applicant selects the polygon (§20.30.050.A.3), so AlderCAD tries both methods
  // the code allows and keeps the one that leaves the most room under the limits:
  //  - the SLOPED-SITE option: a polygon over 5% (the smallest one when it already is,
  //    else the steepest within the size cap) → the side-property-line plane;
  //  - the CORNER-AVERAGE option: a polygon at 5% or less, turned to sit on the highest
  //    ground it can (the highest four-corner average) → that average.
  // Every candidate encloses the footprint, stays inside the buildable area and is no
  // more than POLYGON_AREA_CAP times the smallest polygon's area: the code wants the
  // polygon to "most closely approximate" the footprint, so it may not balloon to reach
  // favorable ground. Measured on Santiago: the flattest polygon (1.41%) would have
  // measured the house ~1 ft TALLER than the sloped-site plane, so "flattest" is not
  // "best"; both are computed and compared.
  const smallestArea = Math.abs(signedArea(poly.quad))
  const maxArea = smallestArea * POLYGON_AREA_CAP
  const fitRing = insideBuildable === false ? null : envRing
  const sample = (q: Pt2[]): GradeSample[] => q.map((pt) => ({ pt, ...grade(pt) }))
  const slopeOfQ = (q: Pt2[]) => polygonSlope(sample(q)).slope
  const avgOfQ = (q: Pt2[]) => sample(q).reduce((s, c) => s + c.zFt, 0) / 4
  const T = HEIGHT_SLOPE_THRESHOLD

  interface Option { quad: Pt2[]; method: 'flat' | 'slope'; levelGradeFt: number | null; gradeAt: (pt: Pt2) => number }
  let chosen: Option | null = null
  let chosenChecks: ReturnType<typeof runChecks> | null = null
  let alternative: HeightAlternative | null = null
  const tightest = (c: ReturnType<typeof runChecks>) =>
    Math.min(...[c.highestRoof, c.highestChimney, c.worstFlat, c.worstSloped].filter((x): x is HeightCheck => !!x).map((x) => x.marginFt), Infinity)

  if (settings.method === 'auto') {
    const options: Option[] = []
    if (sideSamples) {
      let q = poly.quad
      if (slopeOfQ(q) <= T) q = tunePolygon(footprint[0], fitRing, q, slopeOfQ, maxArea)
      if (slopeOfQ(q) > T) options.push({ quad: q, method: 'slope', levelGradeFt: null, gradeAt: sideLinePlane(sideSamples.s1, sideSamples.s2) })
    }
    {
      let q = poly.quad
      if (slopeOfQ(q) > T) q = tunePolygon(footprint[0], fitRing, q, (x) => -slopeOfQ(x), maxArea)
      if (slopeOfQ(q) <= T) {
        q = tunePolygon(footprint[0], fitRing, q, (x) => (slopeOfQ(x) <= T ? avgOfQ(x) : -Infinity), maxArea)
        const g = avgOfQ(q)
        options.push({ quad: q, method: 'flat', levelGradeFt: g, gradeAt: () => g })
      }
    }
    if (options.length === 2) {
      const scored = options.map((o) => {
        const checks = runChecks(o.gradeAt, o.method)
        return { o, checks, t: tightest(checks) }
      })
      // most allowable height = the most room left at the tightest point; a tie keeps
      // the corner average (the simpler one to check)
      scored.sort((a, b) => (b.t - a.t) || (a.o.method === 'flat' ? -1 : 1))
      chosen = scored[0].o
      chosenChecks = scored[0].checks
      const other = scored[1]
      const oc = sample(other.o.quad)
      alternative = {
        method: other.o.method, slope: polygonSlope(oc).slope, levelGradeFt: other.o.levelGradeFt,
        tightestMarginFt: Number.isFinite(other.t) ? other.t : null,
        chosenTightestMarginFt: Number.isFinite(scored[0].t) ? scored[0].t : null,
      }
    } else if (options.length === 1) {
      chosen = options[0]
    }
  }

  let method: HeightMethod
  let quad: Pt2[]
  let levelGradeFt: number | null = null
  let side1: SidePoint[] | null = null
  let side2: SidePoint[] | null = null
  let gradeAt: (pt: Pt2) => number = () => 0
  if (chosen) {
    method = chosen.method
    quad = chosen.quad
    levelGradeFt = chosen.levelGradeFt
    gradeAt = chosen.gradeAt
    if (method === 'slope' && sideSamples) { side1 = sideSamples.s1; side2 = sideSamples.s2 }
  } else {
    // a method set by hand, or nothing qualified: the smallest polygon, the old way
    quad = poly.quad
    const s = slopeOfQ(quad)
    method = settings.method === 'auto' ? (s > T ? 'slope' : 'flat') : settings.method
    if (method === 'slope') {
      if (!sideSamples) {
        notes.push('The sloped-site method needs a property line with front, rear and both side edges assigned. Using the four-corner average instead.')
        method = 'flat'
      } else {
        side1 = sideSamples.s1; side2 = sideSamples.s2
        gradeAt = sideLinePlane(side1, side2)
      }
    }
    if (method === 'slab') levelGradeFt = toProjectFt(model, settings.slabRealElevFt)
    else if (method === 'flat') levelGradeFt = avgOfQ(quad)
    if (levelGradeFt !== null) { const g = levelGradeFt; gradeAt = () => g }
  }
  if (side1 && side2 && [...side1, ...side2].some((p) => p.approx)) notes.push('A side-line point falls outside the survey; its elevation is the nearest surveyed point.')

  const corners: GradeSample[] = sample(quad)
  if (corners.some((c) => c.approx)) notes.push('A polygon corner falls outside the survey; its elevation is the nearest surveyed point.')
  const { slope, hi, lo } = polygonSlope(corners)
  const codeMethod: 'flat' | 'slope' = slope > T ? 'slope' : 'flat'
  const polygonAreaSf = Math.abs(signedArea(quad))

  // ---- high-point check ----
  function runChecks(gradeAt: (pt: Pt2) => number, method: HeightMethod) {
  // Matt (2026-10-07): the drawing must show the HIGHEST ROOF POINT and the HIGHEST
  // CHIMNEY POINT (to the cap when there is one), not just whichever point sits
  // closest to its limit, and flag anything over. Chimneys may rise 2' above the limit
  // (§20.30.060.D.4.a).
  const slopedRings: Pt2[][] = []
  /** each roof's faces with their class and top-surface plane — a roof's OWN pieces
   *  (rake trim, barge boards, ridge caps reaching a few inches past the face outline)
   *  take the class of that roof's nearest face, never the generic covered-by test */
  interface Face { ring: Pt2[]; sloped: boolean; topAt: (pt: Pt2) => number }
  const roofFaces = new Map<string, Face[]>()
  for (const el of els) {
    if (el.category !== 'roof' || demolished(el)) continue
    const sk = roofSkeletonClipped(el, byId)
    const t = el.assembly ? assemblyThickness(el.assembly) : 0
    const faces = (sk?.faces ?? []).map((f): Face => {
      const m = f.plane.risePerRun
      const plumb = t * Math.sqrt(1 + m * m)
      return {
        ring: f.ring,
        sloped: Math.abs(m) >= SLOPED_ROOF_MIN_PITCH - 1e-6,
        topAt: (pt) => f.plane.zOriginFt + m * ((pt[0] - f.plane.origin[0]) * f.plane.uphill[0] + (pt[1] - f.plane.origin[1]) * f.plane.uphill[1]) + plumb,
      }
    })
    roofFaces.set(el.id, faces)
    for (const f of faces) if (f.sloped) slopedRings.push(f.ring)
  }
  const underSloped = (pt: Pt2) => slopedRings.some((r) => pointInRing(pt, r) || distToRing(pt, r) < 0.25)
  const roofPieceSloped = (roofId: string, pt: Pt2): boolean | null => {
    const faces = roofFaces.get(roofId)
    if (!faces?.length) return null
    if (faces.every((f) => f.sloped)) return true
    if (faces.every((f) => !f.sloped)) return false
    let best = Infinity, sloped = false
    for (const f of faces) {
      const d = pointInRing(pt, f.ring) ? 0 : distToRing(pt, f.ring)
      if (d < best) { best = d; sloped = f.sloped }
    }
    return sloped
  }
  /** the roof's top surface directly over a plan point, and whether that face is sloped */
  const roofOver = (pt: Pt2): { z: number; sloped: boolean } | null => {
    let out: { z: number; sloped: boolean } | null = null
    for (const faces of roofFaces.values()) for (const f of faces) {
      if (!pointInRing(pt, f.ring)) continue
      const z = f.topAt(pt)
      if (!out || z > out.z) out = { z, sloped: f.sloped }
    }
    return out
  }
  const limitOf = (sloped: boolean) => (sloped ? settings.slopedLimitFt : settings.flatLimitFt)
  const checkAt = (s: ElementSolid, role: HeightCheck['role'], pt: Pt2, z: number, sloped: boolean, allowanceFt = 0): HeightCheck => {
    const g = gradeAt(pt)
    const h = z - g
    const limit = limitOf(sloped)
    return {
      kind: sloped ? 'sloped' : 'flat', role, elementId: s.elementId, category: s.category,
      pt, zFt: z, gradeFt: g, heightFt: h, limitFt: limit, allowanceFt, marginFt: limit + allowanceFt - h,
    }
  }

  // every principal-building piece, with its plan box and its single highest point.
  // Only the PRINCIPAL building is held to this datum: a piece counts when its plan
  // center sits on or within PIECE_REACH_FT of the footprint (eaves overhang it). A
  // detached garage, ADU or pergola is an accessory structure, measured from existing
  // grade under its own rule (§20.30.060.B.3.c) — not checked here.
  interface Piece { s: ElementSolid; el: ResolvedElement; box: [number, number, number, number]; top: [number, number, number]; chimney: boolean }
  const PIECE_REACH_FT = 6
  const principal = footprint[0]
  let skippedDetached = 0
  const parts: Piece[] = []
  for (const s of solids ?? []) {
    if (!HEIGHT_CATEGORIES.has(s.category) && s.category !== 'object') continue
    const el = byId.get(s.elementId)
    if (!el || demolished(el)) continue
    let top: [number, number, number] = [0, 0, -Infinity]
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity
    const take = (x: number, y: number, z: number) => {
      if (x < x0) x0 = x
      if (x > x1) x1 = x
      if (y < y0) y0 = y
      if (y > y1) y1 = y
      if (z > top[2]) top = [x, y, z]
    }
    if (s.mesh) {
      const p = s.mesh.positions
      for (let i = 0; i + 2 < p.length; i += 3) take(p[i], p[i + 1], p[i + 2])
    }
    if (!s.meshOnly) for (const ring of s.outlines) for (const q of ring) take(q[0], q[1], s.zTop)
    if (!Number.isFinite(top[2]) || x0 > x1) continue
    const mid: Pt2 = [(x0 + x1) / 2, (y0 + y1) / 2]
    if (!pointInRing(mid, principal) && distToRing(mid, principal) > PIECE_REACH_FT) {
      if (s.category !== 'object') skippedDetached++
      continue
    }
    parts.push({ s, el, box: [x0, y0, x1, y1], top, chimney: false })
  }

  // CHIMNEYS: a fireplace's flue pieces (cap / shroud / spark arrestor) seed chimney
  // zones; walls overlapping a zone are its chase, and any wall that stands proud of a
  // SLOPED roof is a chimney too (a masonry chimney with no fireplace object; on a flat
  // roof a proud wall is a parapet, held to the flat limit). Objects inside a zone (a
  // cap modeled on its own) ride with the chimney. Every other object is furniture or
  // equipment and is not measured here.
  const typeName = (el: ResolvedElement) => getType(model, el.typeId)?.name ?? ''
  type Box = [number, number, number, number]
  const zones: Box[] = []
  const widen = (b: Box, d: number): Box => [b[0] - d, b[1] - d, b[2] + d, b[3] + d]
  const overlaps = (a: Box, b: Box) => a[0] <= b[2] && b[0] <= a[2] && a[1] <= b[3] && b[1] <= a[3]
  for (const p of parts) {
    if (p.s.category !== 'object') continue
    const piece = p.s.groupKey.split('|').pop() ?? ''
    if (FLUE_RE.test(piece) || CHIMNEY_TYPE_RE.test(typeName(p.el))) { p.chimney = true; zones.push(widen(p.box, CHIMNEY_REACH_FT)) }
  }
  for (const p of parts) {
    if (p.s.category !== 'wall') continue
    if (zones.some((z) => overlaps(p.box, z))) { p.chimney = true; continue }
    const over = roofOver([p.top[0], p.top[1]])
    if (over && over.sloped && p.top[2] > over.z + 0.5) p.chimney = true
  }
  for (const p of parts) if (p.s.category === 'wall' && p.chimney) zones.push(widen(p.box, 1))
  for (const p of parts) {
    if (p.s.category === 'object' && !p.chimney && zones.some((z) => overlaps(p.box, z))) p.chimney = true
  }

  // the highest roof point and the highest chimney point
  let highestRoof: HeightCheck | null = null
  let highestChimney: HeightCheck | null = null
  for (const p of parts) {
    const pt: Pt2 = [p.top[0], p.top[1]]
    if (p.chimney) {
      if (!highestChimney || p.top[2] > highestChimney.zFt) highestChimney = checkAt(p.s, 'chimney', pt, p.top[2], underSloped(pt), settings.chimneyAllowanceFt)
    } else if (p.s.category === 'roof') {
      if (!highestRoof || p.top[2] > highestRoof.zFt) highestRoof = checkAt(p.s, 'roof', pt, p.top[2], roofPieceSloped(p.s.elementId, pt) ?? underSloped(pt))
    }
  }

  // and, for everything that is NOT a chimney or an object, the point closest to (or
  // furthest over) its limit in each class: on a sloped lot the highest point is not
  // always the governing one, since the grade under it moves
  let worstFlat: HeightCheck | null = null
  let worstSloped: HeightCheck | null = null
  const consider = (s: ElementSolid, x: number, y: number, z: number) => {
    const pt: Pt2 = [x, y]
    const h = z - gradeAt(pt)
    const beatsFlat = !worstFlat || settings.flatLimitFt - h < worstFlat.marginFt
    const beatsSloped = !worstSloped || settings.slopedLimitFt - h < worstSloped.marginFt
    if (!beatsFlat && !beatsSloped) return
    const sloped = (s.category === 'roof' ? roofPieceSloped(s.elementId, pt) : null) ?? underSloped(pt)
    const check = checkAt(s, 'other', pt, z, sloped)
    if (sloped) { if (!worstSloped || check.marginFt < worstSloped.marginFt) worstSloped = check }
    else if (!worstFlat || check.marginFt < worstFlat.marginFt) worstFlat = check
  }
  // tallest pieces first, and a piece that cannot beat either current worst is skipped
  // whole: a real model carries ~1M roof/siding vertices (measured), nearly all low
  const scan: { s: ElementSolid; bound: number }[] = []
  for (const p of parts) {
    if (p.chimney || p.s.category === 'object') continue
    const [x0, y0, x1, y1] = p.box
    // lowest grade under the piece's box (corners and center, less a foot of slack for
    // the blended plane's curvature) gives an upper bound on any of its heights
    const gMin = Math.min(gradeAt([x0, y0]), gradeAt([x1, y0]), gradeAt([x1, y1]), gradeAt([x0, y1]), gradeAt([(x0 + x1) / 2, (y0 + y1) / 2])) - (method === 'slope' ? 1 : 0)
    scan.push({ s: p.s, bound: p.top[2] - gMin })
  }
  scan.sort((a, b) => b.bound - a.bound)
  for (const { s, bound } of scan) {
    const flatDone = worstFlat && bound <= settings.flatLimitFt - (worstFlat as HeightCheck).marginFt
    const slopedDone = worstSloped && bound <= settings.slopedLimitFt - (worstSloped as HeightCheck).marginFt
    if (flatDone && slopedDone) continue
    if (s.mesh) {
      const p = s.mesh.positions
      for (let i = 0; i + 2 < p.length; i += 3) consider(s, p[i], p[i + 1], p[i + 2])
    }
    if (!s.meshOnly) for (const ring of s.outlines) for (const q of ring) consider(s, q[0], q[1], s.zTop)
  }
  return { highestRoof, highestChimney, worstFlat, worstSloped, skippedDetached, parts }
  }
  const checks = chosenChecks ?? runChecks(gradeAt, method)

  // ---- the HOA limit: same grade, its own number, tallest above grade ----
  // On a sloped plane the highest point is not always the tallest above grade, so every
  // roof vertex is measured (once, on the chosen grade only); chimneys by their tops.
  let hoa: HoaHeightCheck | null = null
  if (settings.hoaLimitFt != null) {
    const limit = settings.hoaLimitFt
    const allow = settings.hoaChimneyAllowanceFt
    const mk = (p: (typeof checks.parts)[number], role: HeightCheck['role'], pt: Pt2, z: number): HeightCheck => {
      const g = gradeAt(pt)
      const a = role === 'chimney' ? (allow ?? 0) : 0
      return {
        kind: role === 'roof' ? 'sloped' : 'flat', role, allowanceFt: a, elementId: p.s.elementId, category: p.s.category,
        pt, zFt: z, gradeFt: g, heightFt: z - g, limitFt: limit, marginFt: limit + a - (z - g),
      }
    }
    let roof: HeightCheck | null = null
    let chimney: HeightCheck | null = null
    for (const p of checks.parts) {
      if (p.chimney) {
        // CC&Rs silent on chimneys: not held to the HOA limit (a chimney must clear the roof)
        if (allow == null) continue
        const pt: Pt2 = [p.top[0], p.top[1]]
        const h = p.top[2] - gradeAt(pt)
        if (!chimney || h > chimney.heightFt) chimney = mk(p, 'chimney', pt, p.top[2])
        continue
      }
      if (p.s.category !== 'roof') continue
      let best = -Infinity, bx = 0, by = 0, bz = 0
      const take = (x: number, y: number, z: number) => {
        const h = z - gradeAt([x, y])
        if (h > best) { best = h; bx = x; by = y; bz = z }
      }
      if (p.s.mesh) {
        const q = p.s.mesh.positions
        for (let i = 0; i + 2 < q.length; i += 3) take(q[i], q[i + 1], q[i + 2])
      }
      if (!p.s.meshOnly) for (const ring of p.s.outlines) for (const q of ring) take(q[0], q[1], p.s.zTop)
      if (best > -Infinity && (!roof || best > roof.heightFt)) roof = mk(p, 'roof', [bx, by], bz)
    }
    hoa = { name: settings.hoaName, label: settings.hoaLabel, citation: settings.hoaCitation, limitFt: limit, allowanceFt: allow, roof, chimney }
  }
  if (!solids) notes.push('Building geometry is not ready yet; the high-point check is pending.')
  // what the research could not resolve (an HOA rule missing, a lot number unreadable)
  for (const p of settings.problems) notes.push(p)
  if (checks.skippedDetached) notes.push('Detached structures are not checked here: accessory structures are measured from existing grade.')

  return {
    ok: true, settings, footprint, others: pieces.slice(1), corners, slope, slopeHigh: hi, slopeLow: lo, codeMethod, method,
    levelGradeFt, side1, side2, gradeAt, insideBuildable,
    worstFlat: checks.worstFlat, worstSloped: checks.worstSloped, highestRoof: checks.highestRoof, highestChimney: checks.highestChimney,
    polygonAreaSf, smallestPolygonAreaSf: smallestArea, areaCap: POLYGON_AREA_CAP, alternative, hoa, notes,
  }
}

